// Entry point for the build script in your package.json
import "@hotwired/turbo-rails";
import "./confirm_dialog";
import { Application } from "@hotwired/stimulus";
import { initializeReferenceImages } from './reference_images';
import Sortable from 'sortablejs';
import { initializeLightbox } from "./lightbox";
import { initializeTrixImages } from "./trix_images";
import "trix";

// Chart.js + boxplot plugin (used by admin Release Variance visualizations)
import { Chart, registerables } from "chart.js";
import { BoxPlotController, BoxAndWiskers } from "@sgratzl/chartjs-chart-boxplot";
Chart.register(...registerables, BoxPlotController, BoxAndWiskers);
// Expose Chart globally for existing controllers
window.Chart = Chart;

// Make lightbox function available globally
window.initializeLightbox = initializeLightbox;

// Customize Trix toolbar before initialization
document.addEventListener("DOMContentLoaded", () => {
  // Initialize reference edit toggle
  initializeReferenceEditToggle();
  
  // Remove unwanted buttons and modify heading behavior
  if (typeof Trix !== 'undefined') {
    // Remove unwanted toolbar items
    delete Trix.config.blockAttributes.heading1;
    delete Trix.config.blockAttributes.code;
    delete Trix.config.textAttributes.strike;
    
    // Add custom heading configurations
    Trix.config.blockAttributes.h2 = {
      tagName: "h2",
      terminal: true,
      breakOnReturn: true,
      group: false
    };
    
    Trix.config.blockAttributes.h3 = {
      tagName: "h3", 
      terminal: true,
      breakOnReturn: true,
      group: false
    };
    
    // Hide unwanted toolbar buttons via CSS
    const style = document.createElement('style');
    style.textContent = `
      trix-toolbar .trix-button--icon-strike,
      trix-toolbar .trix-button--icon-code,
      trix-toolbar .trix-button--icon-increase-nesting-level,
      trix-toolbar .trix-button--icon-decrease-nesting-level {
        display: none !important;
      }
      /* Allow toolbar buttons to wrap to a second row instead of overflowing
         the toolbar's right edge in narrow containers (e.g. the new custom
         figure form, mobile widths). Default Trix CSS uses flex-wrap: nowrap.
         The trix-toolbar itself is a default-display custom element, so we
         explicitly make it a block-level container that respects its parent's
         width. */
      trix-toolbar {
        display: block !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      trix-toolbar .trix-button-row {
        flex-wrap: wrap !important;
        justify-content: flex-start !important;
        row-gap: 0.5rem !important;
        overflow: visible !important;
      }
      /* Hide the spacer between button groups when wrapping; with
         justify-content: flex-start, groups stay clumped together. */
      trix-toolbar .trix-button-group-spacer {
        display: none !important;
      }
      /* Pass hover events from icon children through to the button so the
         button title attribute shows as a tooltip even when hovering
         directly over an inner icon / strong / em element. */
      trix-toolbar .trix-button > * {
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }
});

// Initialize Trix with custom behavior
document.addEventListener("trix-initialize", (event) => {
  const editor = event.target;
  
  // Add custom toolbar if not already added
  if (!editor.toolbarElement.dataset.customized) {
    customizeTrixToolbar(editor.toolbarElement);
    editor.toolbarElement.dataset.customized = "true";
  }
});

function customizeTrixToolbar(toolbar) {
  // Find and modify heading button
  const headingButton = toolbar.querySelector('[data-trix-attribute="heading1"]');
  if (headingButton) {
    // Clone the heading button to create H2 and H3 buttons
    const h2Button = headingButton.cloneNode(true);
    const h3Button = headingButton.cloneNode(true);
    
    // Modify H2 button. Strip the trix-button--icon* classes inherited
    // from the cloned heading1 button so its ::before icon and text-indent
    // rules don't fight with the visible "H2" text/tooltip.
    h2Button.className = 'trix-button';
    h2Button.setAttribute('data-trix-attribute', 'h2');
    h2Button.setAttribute('title', 'Heading 2');
    h2Button.setAttribute('aria-label', 'Heading 2');
    h2Button.textContent = 'H2';

    // Modify H3 button
    h3Button.className = 'trix-button';
    h3Button.setAttribute('data-trix-attribute', 'h3');
    h3Button.setAttribute('title', 'Heading 3');
    h3Button.setAttribute('aria-label', 'Heading 3');
    h3Button.textContent = 'H3';
    
    // Replace original heading button with H2, then add H3
    headingButton.parentNode.replaceChild(h2Button, headingButton);
    h2Button.parentNode.insertBefore(h3Button, h2Button.nextSibling);
  }

  // Replace verbose text labels with compact icons so the toolbar fits in
  // narrow containers (e.g. the new custom figure form). Heading buttons
  // (H2/H3) keep their text since the letters convey size cleanly.
  const iconReplacements = [
    { selector: '[data-trix-attribute="bold"]',     icon: '<strong>B</strong>',         title: 'Bold' },
    { selector: '[data-trix-attribute="italic"]',   icon: '<em>I</em>',                 title: 'Italic' },
    { selector: '[data-trix-action="link"]',        icon: '<i class="fas fa-link"></i>',         title: 'Link' },
    { selector: '[data-trix-attribute="quote"]',    icon: '<i class="fas fa-quote-right"></i>',  title: 'Quote' },
    { selector: '[data-trix-attribute="bullet"]',   icon: '<i class="fas fa-list-ul"></i>',      title: 'Bulleted list' },
    { selector: '[data-trix-attribute="number"]',   icon: '<i class="fas fa-list-ol"></i>',      title: 'Numbered list' },
    { selector: '[data-trix-action="attachFiles"]', icon: '<i class="fas fa-paperclip"></i>',    title: 'Attach files' },
    { selector: '[data-trix-action="undo"]',        icon: '<i class="fas fa-rotate-left"></i>',  title: 'Undo' },
    { selector: '[data-trix-action="redo"]',        icon: '<i class="fas fa-rotate-right"></i>', title: 'Redo' },
  ];

  iconReplacements.forEach(({ selector, icon, title }) => {
    const btn = toolbar.querySelector(selector);
    if (btn) {
      btn.innerHTML = icon;
      btn.setAttribute('title', title);
      btn.setAttribute('aria-label', title);
    }
  });
}

// Initialize image upload functionality for Trix editors
function initializeTrixImageUpload() {
  // Handle drag and drop for Trix editors
  document.addEventListener("trix-file-accept", (event) => {
    const { file } = event;
    if (file && file.type.startsWith("image/")) {
      // Accept image files
      return;
    }
    // Reject non-image files
    event.preventDefault();
  });
}

// Initialize preview button functionality
function initializePreviewButton() {
  const previewButton = document.getElementById('preview-button');
  const previewModal = document.getElementById('preview-modal');
  const closePreview = document.getElementById('close-preview');
  const previewContent = document.getElementById('preview-content');
  
  if (!previewButton || !previewModal || !closePreview || !previewContent) {
    return; // Not on a page with preview functionality
  }
  
  // Show preview modal
  previewButton.addEventListener('click', async (e) => {
    e.preventDefault();
    
    // Get content from Trix editor
    const trixEditor = document.querySelector('trix-editor');
    if (!trixEditor) {
      alert('No editor found');
      return;
    }
    
    const content = trixEditor.value || trixEditor.innerHTML;
    if (!content.trim()) {
      alert('No content to preview');
      return;
    }
    
    try {
      // Get figure ID from the button data attribute
      const figureId = previewButton.dataset.figureId;
      
      // Send content to server for markdown rendering
      const response = await fetch('/figures/preview_markdown', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ 
          content: content,
          figure_id: figureId 
        })
      });
      
      if (response.ok) {
        const result = await response.text();
        previewContent.querySelector('.prose').innerHTML = result;
        previewModal.classList.remove('hidden');
        
        // Initialize Trix images and lightbox for the preview content
        setTimeout(() => {
          initializeTrixImages();
          initializeLightbox();
        }, 100);
      } else {
        // Fallback: show raw HTML content
        previewContent.querySelector('.prose').innerHTML = content;
        previewModal.classList.remove('hidden');
        
        // Initialize Trix images and lightbox for the preview content
        setTimeout(() => {
          initializeTrixImages();
          initializeLightbox();
        }, 100);
      }
    } catch (error) {
      console.error('Preview error:', error);
      // Fallback: show raw HTML content
      previewContent.querySelector('.prose').innerHTML = content;
      previewModal.classList.remove('hidden');
      
      // Initialize Trix images and lightbox for the preview content
      setTimeout(() => {
        initializeTrixImages();
        initializeLightbox();
      }, 100);
    }
  });
  
  // Close preview modal
  closePreview.addEventListener('click', () => {
    previewModal.classList.add('hidden');
  });
  
  // Close modal on backdrop click
  previewModal.addEventListener('click', (e) => {
    if (e.target === previewModal) {
      previewModal.classList.add('hidden');
    }
  });
  
  // Close modal on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !previewModal.classList.contains('hidden')) {
      previewModal.classList.add('hidden');
    }
  });
}
import CollectionPhotosController from "./controllers/collection_photos_controller";
import SortableController from "./controllers/sortable_controller";
import ReorderableController from "./controllers/reorderable_controller";
import ToggleController from "./controllers/toggle_controller";
import SiteHeaderController from "./controllers/site_header_controller";
import PhotoDropController from "./controllers/photo_drop_controller";
import FramePollController from "./controllers/frame_poll_controller";
import FoldOnPhoneController from "./controllers/fold_on_phone_controller";
import AdmDrawerController from "./controllers/adm_drawer_controller";
import PartSearchController from "./controllers/part_search_controller";
import FigurePickController from "./controllers/figure_pick_controller";
import PropertyPickerController from "./controllers/property_picker_controller";
import PartSummaryController from "./controllers/part_summary_controller";
import MediaBoardController from "./controllers/media_board_controller";
import SectionNavController from "./controllers/section_nav_controller";
import RtfConverterController from "./controllers/rtf_converter_controller";
import JobStatusController from "./controllers/job_status_controller";
import ToastController from "./controllers/toast_controller";
import MergeUndoController from "./controllers/merge_undo_controller";
import RwSureController from "./controllers/rw_sure_controller";
import LoginModalController from "./controllers/login_modal_controller";
import UsernameModalController from "./controllers/username_modal_controller";
import UsernameCheckController from "./controllers/username_check_controller";
import ExpandableSearchController from "./controllers/expandable_search_controller";
import ReleaseVarianceController from "./controllers/release_variance_controller";
import TimelineController from "./controllers/timeline_controller";
import ProductionChartController from "./controllers/production_chart_controller";
import NumberMenuController from "./controllers/number_menu_controller";
import ReissuesChartController from "./controllers/reissues_chart_controller";
import CollaboratorTypeaheadController from "./controllers/collaborator_typeahead_controller";
import ImageUploaderController from "./controllers/image_uploader_controller";
import InlineEditorController from "./controllers/inline_editor_controller";
import PartOptionsSyncController from "./controllers/part_options_sync_controller";
import AssigneeOptionsSyncController from "./controllers/assignee_options_sync_controller";
import StatusPillController from "./controllers/status_pill_controller";
import LazyAvatarController from "./controllers/lazy_avatar_controller";
import TradingCardController from "./controllers/trading_card_controller";
import DifficultyPickerController from "./controllers/difficulty_picker_controller";
import BuildPhotosViewController from "./controllers/build_photos_view_controller";
import InviteModalController from "./controllers/invite_modal_controller";
import CollaboratorEmailEditController from "./controllers/collaborator_email_edit_controller";
import PhotoAnnotatorController from "./controllers/photo_annotator_controller";
import ShowcaseBlueprintController from "./controllers/showcase_blueprint_controller";
import ClipboardPasteController from "./controllers/clipboard_paste_controller";
import PriceHistoryController from "./controllers/price_history_controller";
import ShelfReelController from "./controllers/shelf_reel_controller";
import RwPeekController from "./controllers/rw_peek_controller";
import RwMergeDestController from "./controllers/rw_merge_dest_controller";
import RwOptionSumController from "./controllers/rw_option_sum_controller";
import RwOptionCheckController from "./controllers/rw_option_check_controller";
import RwOptionHelpController from "./controllers/rw_option_help_controller";
import RwLinkNameController from "./controllers/rw_link_name_controller";
import RwMergeTargetsController from "./controllers/rw_merge_targets_controller";
import RwMergeOptionsController from "./controllers/rw_merge_options_controller";
import RwMergeHelpController from "./controllers/rw_merge_help_controller";
import RwPeekPicturesController from "./controllers/rw_peek_pictures_controller";
import RwInboxCardController from "./controllers/rw_inbox_card_controller";
import RwPageOptionsController from "./controllers/rw_page_options_controller";
import RwFigurePickController from "./controllers/rw_figure_pick_controller";
import RwLookupController from "./controllers/rw_lookup_controller";
import NumberingLinesController from "./controllers/numbering_lines_controller";
import NbMergeController from "./controllers/nb_merge_controller";
import RwGridController from "./controllers/rw_grid_controller";
import AdmSheetController from "./controllers/adm_sheet_controller";
import RwShelfController from "./controllers/rw_shelf_controller";
import RwOptionViewController from "./controllers/rw_option_view_controller";
import RwShareController from "./controllers/rw_share_controller";
import RwShotController from "./controllers/rw_shot_controller";
import RwFlipController from "./controllers/rw_flip_controller";
import WorkbenchFillController from "./controllers/workbench_fill_controller";
import CardVideoController from "./controllers/card_video_controller";
import FigureImportController from "./controllers/figure_import_controller";
import PictureTrayController from "./controllers/picture_tray_controller";
import YoutubeReviewsController from "./controllers/youtube_reviews_controller";
import ReviewPlayerController from "./controllers/review_player_controller";
import ReviewWallController from "./controllers/review_wall_controller";
import RsTickedController from "./controllers/rs_ticked_controller";
import DataMapController from "./controllers/data_map_controller";
import DpBulkController from "./controllers/dp_bulk_controller";
import DpBoxesController from "./controllers/dp_boxes_controller";
import DpFoundController from "./controllers/dp_found_controller";
import DpCardController from "./controllers/dp_card_controller";

// Register Stimulus controllers
const application = Application.start();
application.register("collection-photos", CollectionPhotosController);
application.register("sortable", SortableController);
application.register("reorderable", ReorderableController);
application.register("release-variance", ReleaseVarianceController);
application.register("toggle", ToggleController);
application.register("site-header", SiteHeaderController);
application.register("photo-drop", PhotoDropController);
application.register("frame-poll", FramePollController);
application.register("workbench-fill", WorkbenchFillController);
application.register("fold-on-phone", FoldOnPhoneController);
application.register("adm-drawer", AdmDrawerController);
application.register("part-search", PartSearchController);
application.register("figure-pick", FigurePickController);
application.register("property-picker", PropertyPickerController);
application.register("part-summary", PartSummaryController);
application.register("media-board", MediaBoardController);
application.register("section-nav", SectionNavController);
application.register("rtf-converter", RtfConverterController);
application.register("figure-import", FigureImportController);
application.register("picture-tray", PictureTrayController);
application.register("youtube-reviews", YoutubeReviewsController);
application.register("review-player", ReviewPlayerController);
application.register("review-wall", ReviewWallController);
application.register("data-map", DataMapController);
application.register("rs-ticked", RsTickedController);
application.register("job-status", JobStatusController);
application.register("toast", ToastController);
application.register("dp-bulk", DpBulkController);
application.register("dp-boxes", DpBoxesController);
application.register("dp-found", DpFoundController);
application.register("dp-card", DpCardController);
application.register("merge-undo", MergeUndoController);
application.register("rw-sure", RwSureController);
application.register("login-modal", LoginModalController);
application.register("username-modal", UsernameModalController);
application.register("username-check", UsernameCheckController);
application.register("expandable-search", ExpandableSearchController);
application.register("timeline", TimelineController);
application.register("production-chart", ProductionChartController);
application.register("number-menu", NumberMenuController);
application.register("reissues-chart", ReissuesChartController);
application.register("collaborator-typeahead", CollaboratorTypeaheadController);
application.register("image-uploader", ImageUploaderController);
application.register("inline-editor", InlineEditorController);
application.register("part-options-sync", PartOptionsSyncController);
application.register("assignee-options-sync", AssigneeOptionsSyncController);
application.register("status-pill", StatusPillController);
application.register("lazy-avatar", LazyAvatarController);
application.register("trading-card", TradingCardController);
application.register("difficulty-picker", DifficultyPickerController);
application.register("build-photos-view", BuildPhotosViewController);
application.register("invite-modal", InviteModalController);
application.register("collaborator-email-edit", CollaboratorEmailEditController);
application.register("photo-annotator", PhotoAnnotatorController);
application.register("showcase-blueprint", ShowcaseBlueprintController);
application.register("clipboard-paste", ClipboardPasteController);
application.register("price-history", PriceHistoryController);
application.register("shelf-reel", ShelfReelController);
application.register("rw-peek", RwPeekController);
application.register("rw-merge-dest", RwMergeDestController);
application.register("rw-option-sum", RwOptionSumController);
application.register("rw-option-check", RwOptionCheckController);
application.register("rw-option-help", RwOptionHelpController);
application.register("rw-link-name", RwLinkNameController);
application.register("rw-merge-targets", RwMergeTargetsController);
application.register("rw-merge-options", RwMergeOptionsController);
application.register("rw-merge-help", RwMergeHelpController);
application.register("rw-peek-pictures", RwPeekPicturesController);
application.register("rw-inbox-card", RwInboxCardController);
application.register("rw-page-options", RwPageOptionsController);
application.register("rw-figure-pick", RwFigurePickController);
application.register("rw-lookup", RwLookupController);
application.register("numbering-lines", NumberingLinesController);
application.register("nb-merge", NbMergeController);
application.register("rw-grid", RwGridController);
application.register("adm-sheet", AdmSheetController);
application.register("rw-shelf", RwShelfController);
application.register("rw-option-view", RwOptionViewController);
application.register("rw-share", RwShareController);
application.register("rw-shot", RwShotController);
application.register("rw-flip", RwFlipController);
application.register("card-video", CardVideoController);

// Initialize on turbo:load
document.addEventListener("turbo:load", () => {
  initializeLightbox();
  initializeReferenceImages();
  initializeReferenceEditToggle();
  initializeTrixImageUpload();
  initializePreviewButton();
  initializeTrixImages();
});

// Simple toggle for reference info edit mode
function initializeReferenceEditToggle() {
  const editForm = document.getElementById('reference-edit-form');
  const viewContent = document.getElementById('reference-view-content');
  const showEditButtons = document.querySelectorAll('.show-edit-button');
  const cancelButton = document.getElementById('cancel-edit-button');
  const cancelButtonHeader = document.getElementById('cancel-edit-button-header');
  
  if (!editForm || !viewContent) return;
  
  // Show edit form and hide view content
  function showEditMode() {
    editForm.classList.remove('hidden');
    viewContent.classList.add('hidden');
    
    // Toggle header buttons
    showEditButtons.forEach(button => button.classList.add('hidden'));
    if (cancelButtonHeader) cancelButtonHeader.classList.remove('hidden');
    
    // Scroll to the edit form smoothly
    setTimeout(() => {
      editForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Focus on the Trix editor after scrolling
      setTimeout(() => {
        const trixEditor = document.querySelector('trix-editor');
        if (trixEditor) {
          trixEditor.focus();
        }
      }, 300);
    }, 100);
  }
  
  // Hide edit form and show view content
  function showViewMode() {
    editForm.classList.add('hidden');
    viewContent.classList.remove('hidden');
    
    // Toggle header buttons
    showEditButtons.forEach(button => button.classList.remove('hidden'));
    if (cancelButtonHeader) cancelButtonHeader.classList.add('hidden');
    
    // Scroll back to reference info section with some padding above
    setTimeout(() => {
      const referenceSection = document.querySelector('.bg-blue-900\\/80');
      if (referenceSection) {
        const rect = referenceSection.getBoundingClientRect();
        const offsetTop = window.pageYOffset + rect.top - 50; // 50px padding above
        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    }, 100);
  }
  
  // Add event listeners to all edit buttons
  showEditButtons.forEach(button => {
    button.addEventListener('click', showEditMode);
  });

  // #edit-wiki (a revision's "Edit the wiki") opens it ready to edit.
  if (window.location.hash === '#edit-wiki') showEditMode();
  
  // Add event listeners to cancel buttons
  if (cancelButton) {
    cancelButton.addEventListener('click', showViewMode);
  }
  if (cancelButtonHeader) {
    cancelButtonHeader.addEventListener('click', showViewMode);
  }
}

// Turbo-specific initialization
document.addEventListener("turbo:load", () => {
  const el = document.getElementById('sortable-images');
  if (el && !el.sortable) {
    new Sortable(el, {
      animation: 150,
      onEnd: function () {
        const orderedIds = Array.from(el.children).map(item => item.dataset.id);
        fetch(el.dataset.reorderUrl, {
          method: "PATCH",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ ordered_ids: orderedIds })
        });
      }
    });
    el.sortable = true;
  }
}); 

document.addEventListener("turbo:visit", () => {
  document.getElementById("turbo-loading-indicator")?.classList.remove("hidden");
});
document.addEventListener("turbo:load", () => {
  document.getElementById("turbo-loading-indicator")?.classList.add("hidden");
});
document.addEventListener("turbo:before-cache", () => {
  document.getElementById("turbo-loading-indicator")?.classList.add("hidden");
});
document.addEventListener("turbo:submit-start", () => {
    document.getElementById("turbo-loading-indicator")?.classList.remove("hidden");
});
document.addEventListener("turbo:submit-end", () => {
  document.getElementById("turbo-loading-indicator")?.classList.add("hidden");
});

// Auto-clear any form that opts in via `data-reset-on-submit="true"` after a
// successful Turbo submission. This is for "add another item" forms whose
// turbo_stream response updates a list elsewhere on the page rather than
// replacing the form itself, so without this the inputs stay populated and the
// user has to manually clear them before adding the next item. Calling
// form.reset() also fires a native `reset` event that Stimulus controllers
// (e.g. collaborator-typeahead) can hook into to clear their own UI state.
document.addEventListener("turbo:submit-end", (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.dataset.resetOnSubmit !== "true") return;
  if (!event.detail?.success) return;
  form.reset();
});
document.addEventListener("turbo:before-fetch-request", () => {
    document.getElementById("turbo-loading-indicator")?.classList.remove("hidden");
});
document.addEventListener("turbo:before-fetch-response", () => {
  document.getElementById("turbo-loading-indicator")?.classList.add("hidden");
});

document.addEventListener("click", (e) => {
    // Open or close dropdown
    const menuButton = e.target.closest(".collection-menu-button");
    if (menuButton) {
        const id = menuButton.dataset.figureId;
        const dropdown = document.querySelector(`.collection-dropdown[data-figure-id="${id}"]`);
        if (dropdown) {
        dropdown.classList.toggle("hidden");
        }

        // Close others
        document.querySelectorAll(`.collection-dropdown:not([data-figure-id="${id}"])`).forEach(el => el.classList.add("hidden"));
        return; // Stop bubbling
    }

    // Close dropdowns if clicked elsewhere
    if (!e.target.closest(".collection-dropdown")) {
        document.querySelectorAll(".collection-dropdown").forEach(el => el.classList.add("hidden"));
    }
});

import "trix"
import "@rails/actiontext"

// Direct uploads: file fields rendered with `direct_upload: true` (e.g. the
// custom figure hero video) upload straight to the storage service before
// the form submits, instead of streaming through the app server.
import * as ActiveStorage from "@rails/activestorage"
ActiveStorage.start()
